/**
 * Multi-tab OPFS lock coordinator.
 *
 * THE PROBLEM
 * ───────────
 * Homestead's web build runs SQLite via sqlite-wasm with the OPFS-SAH
 * (Synchronous Access Handle) pool. That handle is an *exclusive* per-origin
 * lock — only one browser tab can hold the database open for writes at a
 * time. The native lock error surfaces as
 *   "NoModificationAllowedError: Access Handle ... already open"
 * which dead-ends the user with a "close the other tab and reload" screen.
 * Users have reasonably reported "I want to open Homestead in two tabs."
 *
 * THE APPROACH
 * ────────────
 * We can't break the underlying lock, but we can make the experience feel
 * like multi-tab works:
 *
 *   1. Every tab opens a BroadcastChannel named "homestead:lock" at boot.
 *   2. When a tab successfully opens the DB, it broadcasts "I have it."
 *   3. When a new tab fails to open the DB (lock conflict), it broadcasts
 *      "I want it." The current holder receives the request, releases by
 *      calling `window.location.reload()` (its DB connection closes during
 *      `beforeunload`, releasing the OPFS handle). The new tab waits a
 *      short beat and reloads itself, succeeding on the second attempt.
 *   4. The holder that yielded comes back as a "second tab" — if the user
 *      focuses it later, IT requests takeover and the cycle reverses.
 *
 * NET EFFECT
 * ──────────
 * The user can open Homestead in N tabs. Whichever one they're focused on
 * is the one that "has" the database. Switching tabs causes the new tab
 * to auto-claim within ~1 second.
 *
 * Native (iOS/Android) skips this module entirely — the constraint is
 * web-only.
 */

import { Platform } from 'react-native';

const CHANNEL = 'homestead:lock';
const TAB_ID = (() => {
  // Stable per-tab identifier so we can ignore our own broadcasts.
  // Math.random() is fine here — we just need uniqueness across N tabs of
  // the same origin in the same session, not cryptographic strength.
  return `tab-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
})();

/**
 * sessionStorage key set RIGHT BEFORE a holder tab reloads to yield. After
 * the reload, _layout.tsx reads this flag and forces the tab straight into
 * the DbErrorScreen "I just yielded" standby state — bypassing SQLiteProvider
 * entirely so the tab doesn't race the new holder for the lock.
 *
 * Without this, the yielding tab reloads first (50ms head start over the new
 * holder's 250ms wait) and almost always grabs the lock back, kicking off
 * a ping-pong loop with the other tab. The flag is cleared whenever THIS
 * tab actively initiates a takeover (button click or focus), so reclaiming
 * works cleanly when the user comes back to this tab.
 */
const YIELDED_KEY = 'homestead:tab-lock:yielded-at';
const YIELDED_TTL_MS = 30 * 60 * 1000;

export function markYieldedNow(): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(YIELDED_KEY, String(Date.now()));
  } catch {
    // Some privacy modes throw on sessionStorage writes. Fallback is the
    // pre-flag behavior — not great, no worse than before.
  }
}

export function clearYielded(): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(YIELDED_KEY);
  } catch {
    // ignore
  }
}

export function wasJustYielded(): boolean {
  if (typeof sessionStorage === 'undefined') return false;
  let ts: string | null = null;
  try {
    ts = sessionStorage.getItem(YIELDED_KEY);
  } catch {
    return false;
  }
  if (!ts) return false;
  const age = Date.now() - Number(ts);
  if (!Number.isFinite(age) || age < 0 || age > YIELDED_TTL_MS) {
    clearYielded();
    return false;
  }
  return true;
}

type LockMessage =
  | { type: 'claim'; from: string }
  | { type: 'request-takeover'; from: string }
  | { type: 'releasing'; from: string };

interface TabLock {
  /** Stable id of this tab. */
  readonly tabId: string;
  /** Broadcast that THIS tab now holds the DB lock. */
  announceClaim(): void;
  /**
   * Ask the current holder (whoever they are) to yield. The holder will
   * reload itself; we then reload too, and on retry we'll get the lock.
   * Safe to call multiple times — the holder ignores duplicate requests
   * once it's already releasing.
   */
  requestTakeover(): void;
  /**
   * Listen for messages from sibling tabs. The provided callback fires
   * for every message that isn't from this tab itself. Returns an
   * unsubscribe function.
   */
  subscribe(fn: (msg: LockMessage) => void): () => void;
  /** True on web with BroadcastChannel available; false otherwise. */
  readonly available: boolean;
}

class WebTabLock implements TabLock {
  readonly tabId = TAB_ID;
  readonly available: boolean;
  private channel: BroadcastChannel | null = null;
  private listeners = new Set<(msg: LockMessage) => void>();

  constructor() {
    this.available =
      typeof globalThis !== 'undefined' &&
      typeof (globalThis as { BroadcastChannel?: unknown }).BroadcastChannel === 'function';
    if (this.available) {
      try {
        this.channel = new BroadcastChannel(CHANNEL);
        this.channel.onmessage = (e: MessageEvent<LockMessage>) => {
          const msg = e.data;
          if (!msg || typeof msg !== 'object') return;
          if (msg.from === this.tabId) return; // ignore our own
          for (const fn of this.listeners) fn(msg);
        };
      } catch {
        // Some browsers (private mode, iframes with strict policies)
        // refuse BroadcastChannel. Fall back to "first tab wins" silently.
        this.channel = null;
      }
    }
  }

  announceClaim(): void {
    this.send({ type: 'claim', from: this.tabId });
  }

  requestTakeover(): void {
    this.send({ type: 'request-takeover', from: this.tabId });
  }

  announceReleasing(): void {
    this.send({ type: 'releasing', from: this.tabId });
  }

  subscribe(fn: (msg: LockMessage) => void): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  private send(msg: LockMessage): void {
    if (!this.channel) return;
    try {
      this.channel.postMessage(msg);
    } catch {
      // Browsers can throw if the channel is in a detached state. Nothing
      // to do — we can only operate on best-effort.
    }
  }
}

class NoopTabLock implements TabLock {
  readonly tabId = TAB_ID;
  readonly available = false;
  announceClaim(): void {}
  requestTakeover(): void {}
  subscribe(): () => void {
    return () => {};
  }
}

/**
 * Singleton. Created lazily so we don't try to touch BroadcastChannel
 * during SSR or in environments where window isn't defined yet.
 */
let instance: TabLock | null = null;

export function getTabLock(): TabLock {
  if (instance) return instance;
  if (Platform.OS !== 'web') {
    instance = new NoopTabLock();
  } else {
    instance = new WebTabLock();
  }
  return instance;
}

/**
 * Mount a global handler that listens for takeover requests from sibling
 * tabs and yields by reloading. Call this once at app boot, AFTER the DB
 * has been opened successfully. Idempotent — re-mounting just replaces
 * the previous subscription.
 */
let unsubscribeHolder: (() => void) | null = null;
export function installHolderYieldHandler(): void {
  if (Platform.OS !== 'web') return;
  const lock = getTabLock();
  if (!lock.available) return;
  // Replace any previous subscription so we don't double-fire on reload
  // (same tab id, but listeners is a fresh Set after each tab boot).
  if (unsubscribeHolder) unsubscribeHolder();
  unsubscribeHolder = lock.subscribe((msg) => {
    if (msg.type === 'request-takeover') {
      // Another tab wants the DB. Announce that we're releasing, then
      // reload — that closes our SQLite worker, which releases the OPFS
      // handle, which lets the requesting tab succeed on its next try.
      if (lock instanceof WebTabLock) lock.announceReleasing();
      // CRITICAL: mark this tab as "just yielded" so the post-reload
      // boot skips SQLiteProvider and lands in standby DbErrorScreen.
      // Without this we race the new holder for the lock after reload
      // and almost always win it back, creating an infinite handoff loop.
      markYieldedNow();
      // Tiny defer so the message is flushed to the channel before we
      // tear down our event loop.
      setTimeout(() => {
        if (typeof window !== 'undefined') window.location.reload();
      }, 50);
    }
  });
  // Tell anyone listening that we hold the lock now.
  lock.announceClaim();
}

/**
 * On the new tab side: ask the current holder to yield, then reload us
 * after a short delay so we can take the lock. Returns a Promise that
 * resolves once we've kicked off the reload — caller doesn't usually
 * await this since the reload terminates execution.
 */
export async function requestTakeoverAndReload(): Promise<void> {
  if (Platform.OS !== 'web') return;
  // This tab is choosing to (re)claim the lock — clear the "just yielded"
  // flag so that after the post-handshake reload we DON'T land back in
  // standby. Without this, a tab that previously yielded would stay in
  // DbErrorScreen forever once the user comes back to it.
  clearYielded();
  const lock = getTabLock();
  if (!lock.available) {
    // No BroadcastChannel: just reload and hope the user closed the
    // other tab themselves. Best we can do.
    if (typeof window !== 'undefined') window.location.reload();
    return;
  }
  // Subscribe briefly so we can react to "releasing" by reloading sooner.
  const unsub = lock.subscribe((msg) => {
    if (msg.type === 'releasing') {
      unsub();
      // Holder is closing — wait a heartbeat for its OPFS handle to
      // actually release before we attempt to acquire.
      setTimeout(() => {
        if (typeof window !== 'undefined') window.location.reload();
      }, 250);
    }
  });
  lock.requestTakeover();
  // Safety net: if nobody answered (no holder, or BroadcastChannel
  // dropped the message), still reload after a generous timeout.
  setTimeout(() => {
    unsub();
    if (typeof window !== 'undefined') window.location.reload();
  }, 1500);
}

/**
 * Focus-aware takeover trigger: fires the takeover handshake only when
 * this tab has keyboard focus (i.e. is the one the user is actually
 * working in). Critical for breaking infinite ping-pong loops.
 *
 * Why focus and not visibility? In multi-window setups (two browser
 * windows side-by-side, dual monitors) BOTH tabs report
 * `document.visibilityState === 'visible'` because both are on-screen.
 * The old visibility-based check would then auto-fire takeover from
 * both tabs at once, and they'd ping-pong the lock forever. By
 * contrast `document.hasFocus()` is true for exactly ONE window at any
 * given time — the one accepting keyboard input — which is the only
 * tab that should be reclaiming the lock automatically.
 *
 * Behavior:
 *   • If document currently has focus → fire takeover after a short
 *     delay (lets the previous holder settle into standby first).
 *   • If not focused → install a focus listener and fire the takeover
 *     the moment the user clicks/focuses this window.
 *   • Returns a teardown function so callers can clean up if they
 *     unmount before the takeover triggers.
 */
export function scheduleTakeoverOnFocus(): () => void {
  if (Platform.OS !== 'web') return () => {};
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return () => {};
  }

  let fired = false;
  const fire = () => {
    if (fired) return;
    fired = true;
    void requestTakeoverAndReload();
  };

  // hasFocus is the ONLY reliable signal here. visibilityState is true
  // for any tab that's currently on-screen, which in side-by-side
  // browser windows means both tabs auto-fire and ping-pong the lock.
  if (typeof document.hasFocus === 'function' && document.hasFocus()) {
    const id = window.setTimeout(fire, 400);
    return () => {
      if (!fired) window.clearTimeout(id);
    };
  }

  // Not focused: wait for the user to actually focus this window before
  // doing anything. This is what breaks the ping-pong loop.
  const onFocus = () => {
    window.removeEventListener('focus', onFocus);
    fire();
  };
  window.addEventListener('focus', onFocus);
  return () => {
    window.removeEventListener('focus', onFocus);
  };
}
